import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

import { isApiError } from '@frontend/api/client';
import { cn } from '@frontend/lib/cn';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  body?: string;
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id'>) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  /** Turns an unknown thrown value into a toast that keeps the server's own message. */
  fromError: (error: unknown, fallbackTitle?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const TONE_CLASS: Record<ToastTone, string> = {
  success: 'border-success-border bg-success-bg',
  error: 'border-error-border bg-error-bg',
  warning: 'border-warning-border bg-warning-bg',
  info: 'border-info-border bg-info-bg',
};

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { ...toast, id }]);
      // Errors stay longer — they usually carry an instruction worth reading.
      window.setTimeout(() => dismiss(id), toast.tone === 'error' ? 12_000 : 5_000);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, body) => push({ tone: 'success', title, ...(body === undefined ? {} : { body }) }),
      error: (title, body) => push({ tone: 'error', title, ...(body === undefined ? {} : { body }) }),
      fromError: (error, fallbackTitle = 'That did not work') => {
        if (isApiError(error)) {
          // The server's message is the specific, actionable one. Keep it.
          push({ tone: 'error', title: fallbackTitle, body: error.message });
          return;
        }
        push({
          tone: 'error',
          title: fallbackTitle,
          body: error instanceof Error ? error.message : 'An unexpected error occurred.',
        });
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed bottom-4 right-4 z-toast flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2"
          aria-live="polite"
          aria-atomic="false"
        >
          {toasts.map((toast) => {
            const Icon = ICONS[toast.tone];
            return (
              <div
                key={toast.id}
                role={toast.tone === 'error' ? 'alert' : 'status'}
                data-testid={`toast-${toast.tone}`}
                className={cn(
                  'pointer-events-auto flex animate-slide-up items-start gap-2 rounded-lg border px-3 py-2.5 shadow-lg',
                  TONE_CLASS[toast.tone],
                )}
              >
                <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-content" />
                <div className="min-w-0 flex-1">
                  <p className="text-row font-medium text-content">{toast.title}</p>
                  {toast.body !== undefined && (
                    <p className="mt-0.5 text-pretty text-body text-content-secondary">{toast.body}</p>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => dismiss(toast.id)}
                  className="shrink-0 rounded-sm p-0.5 text-content-secondary hover:text-content"
                >
                  <X aria-hidden className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (context === null) throw new Error('useToast must be used inside <ToastProvider>.');
  return context;
}
